import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  PortableRuntimeSnapshot,
  PortableSnapshotExportRequest,
  PortableWorkerRequest,
} from '@/v2/export/PortableProject'
import { reportError } from '@/utils/logger'
import ExportWorker from '@/workers/export.worker?worker'

interface WorkerResponse {
  readonly requestId: number
  readonly html?: string
  readonly error?: string
  readonly recoverable?: boolean
}

interface UseExportReturn {
  isExporting: boolean
  exportedHtml: string | null
  error: string | null
  generatePortableHtml: (
    snapshot: PortableRuntimeSnapshot | Promise<PortableRuntimeSnapshot>,
  ) => void
  copyToClipboard: () => Promise<boolean>
  downloadHtml: (filename?: string) => void
  clearExport: () => void
}

async function generateOnMainThread(request: PortableWorkerRequest): Promise<string> {
  const [{ createPortableProject }, { generatePortableHtml }] = await Promise.all([
    import('@/v2/export/PortableProject'),
    import('@/v2/export/generatePortableHtml'),
  ])
  return generatePortableHtml(createPortableProject(request.snapshot))
}

/** Manages one latest-wins export operation and retires workers after every terminal result. */
export function useExport(): UseExportReturn {
  const [isExporting, setIsExporting] = useState(false)
  const [exportedHtml, setExportedHtml] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const workerRef = useRef<Worker | null>(null)
  const requestIdRef = useRef(0)
  const activeRequestIdRef = useRef<number | null>(null)
  const activeRequestRef = useRef<PortableWorkerRequest | null>(null)
  const mountedRef = useRef(true)

  const retireWorker = useCallback(() => {
    workerRef.current?.terminate()
    workerRef.current = null
  }, [])

  const finishError = useCallback((requestId: number, message: string) => {
    if (!mountedRef.current || requestId !== activeRequestIdRef.current) return
    activeRequestIdRef.current = null
    activeRequestRef.current = null
    retireWorker()
    setError(`Failed to generate HTML: ${message}`)
    setIsExporting(false)
  }, [retireWorker])

  const finishSuccess = useCallback((requestId: number, html: string) => {
    if (!mountedRef.current || requestId !== activeRequestIdRef.current) return
    if (html.length === 0) {
      finishError(requestId, 'export returned an empty document')
      return
    }
    activeRequestIdRef.current = null
    activeRequestRef.current = null
    retireWorker()
    setExportedHtml(html)
    setIsExporting(false)
  }, [finishError, retireWorker])

  const recoverOnMainThread = useCallback((requestId: number, cause: unknown) => {
    const request = activeRequestRef.current
    if (request === null || requestId !== activeRequestIdRef.current) return
    // Claim recovery before terminating so duplicate error/messageerror events
    // cannot start multiple full-document generations for the same request.
    activeRequestRef.current = null
    retireWorker()
    reportError('Export worker failed; retrying the portable generator on the main thread.', cause)
    void generateOnMainThread(request).then(
      (html) => { finishSuccess(requestId, html) },
      (fallbackError: unknown) => {
        finishError(
          requestId,
          fallbackError instanceof Error ? fallbackError.message : 'portable fallback failed',
        )
      },
    )
  }, [finishError, finishSuccess, retireWorker])

  const handleWorkerMessage = useCallback((requestId: number, event: MessageEvent<unknown>) => {
    if (requestId !== activeRequestIdRef.current) return
    const data = event.data
    if (typeof data !== 'object' || data === null) {
      recoverOnMainThread(requestId, new Error('Malformed export worker response'))
      return
    }
    const response = data as WorkerResponse
    if (!Number.isSafeInteger(response.requestId) || response.requestId !== requestId) {
      recoverOnMainThread(requestId, new Error('Mismatched export worker response'))
    } else if (response.recoverable === true) {
      recoverOnMainThread(requestId, new Error(response.error ?? 'Recoverable export worker failure'))
    } else if (typeof response.error === 'string' && response.error.length > 0) {
      finishError(response.requestId, response.error)
    } else if (typeof response.html === 'string' && response.html.length > 0) {
      finishSuccess(response.requestId, response.html)
    } else if (response.html === '') {
      recoverOnMainThread(requestId, new Error('Export worker returned an empty document'))
    } else {
      recoverOnMainThread(response.requestId, new Error('Incomplete export worker response'))
    }
  }, [finishError, finishSuccess, recoverOnMainThread])

  const handleWorkerError = useCallback((requestId: number, event: ErrorEvent) => {
    recoverOnMainThread(requestId, event.error ?? event.message)
  }, [recoverOnMainThread])

  const handleWorkerMessageError = useCallback((requestId: number, event: MessageEvent<unknown>) => {
    recoverOnMainThread(requestId, event.data ?? 'worker messageerror')
  }, [recoverOnMainThread])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      activeRequestIdRef.current = null
      activeRequestRef.current = null
      retireWorker()
    }
  }, [retireWorker])

  const startRequest = useCallback((request: PortableWorkerRequest, preparedRequestId?: number) => {
    setIsExporting(true)
    setExportedHtml(null)
    setError(null)
    retireWorker()
    const requestId = preparedRequestId ?? ++requestIdRef.current
    activeRequestIdRef.current = requestId
    activeRequestRef.current = request

    let worker: Worker
    try {
      worker = new ExportWorker()
      worker.addEventListener('message', (event) => { handleWorkerMessage(requestId, event) })
      worker.addEventListener('error', (event) => { handleWorkerError(requestId, event) })
      worker.addEventListener('messageerror', (event) => { handleWorkerMessageError(requestId, event) })
      workerRef.current = worker
      worker.postMessage({ requestId, ...request })
    } catch (workerError) {
      recoverOnMainThread(requestId, workerError)
    }
  }, [handleWorkerError, handleWorkerMessage, handleWorkerMessageError, recoverOnMainThread, retireWorker])

  const generatePortableHtml = useCallback((
    snapshot: PortableRuntimeSnapshot | Promise<PortableRuntimeSnapshot>,
  ) => {
    retireWorker()
    setIsExporting(true)
    setExportedHtml(null)
    setError(null)

    const requestId = ++requestIdRef.current
    activeRequestIdRef.current = requestId
    activeRequestRef.current = null

    void Promise.resolve(snapshot).then(
      (resolvedSnapshot) => {
        if (!mountedRef.current || activeRequestIdRef.current !== requestId) return
        const request: PortableSnapshotExportRequest = {
          exportFormat: 'portable-v2-snapshot',
          snapshot: resolvedSnapshot,
        }
        startRequest(request, requestId)
      },
      (snapshotError: unknown) => {
        finishError(
          requestId,
          snapshotError instanceof Error ? snapshotError.message : 'portable snapshot failed',
        )
      },
    )
  }, [finishError, retireWorker, startRequest])

  const copyToClipboard = useCallback(async (): Promise<boolean> => {
    if (!exportedHtml) return false
    try {
      await navigator.clipboard.writeText(exportedHtml)
      return true
    } catch (copyError) {
      reportError('Copy failed.', copyError)
      setError('Failed to copy to clipboard')
      return false
    }
  }, [exportedHtml])

  const downloadHtml = useCallback((filename = 'calligraphy.html') => {
    if (!exportedHtml) return
    const blob = new Blob([exportedHtml], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }, [exportedHtml])

  const clearExport = useCallback(() => {
    activeRequestIdRef.current = null
    activeRequestRef.current = null
    retireWorker()
    setIsExporting(false)
    setExportedHtml(null)
    setError(null)
  }, [retireWorker])

  return {
    isExporting,
    exportedHtml,
    error,
    generatePortableHtml,
    copyToClipboard,
    downloadHtml,
    clearExport,
  }
}
